import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import "dayjs/locale/it";
import dayjs from "dayjs";
import i18next from "i18next";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { TimeSlot } from "@components/TimeSlotCst";

interface Timezone {
  id: number;
  zone_id: string;
  label: string;
}

interface TimezoneOption {
  value: number;
  label: string;
}

interface DraftSlot {
  slot_start: any;
  slot_end: any;
  exclude_weekend: boolean;
  apply_user_timezone: boolean;
  timezone_id?: number;
}

interface TimeSlotV2CstProps {
  initialTimeSlots: TimeSlot[];
  onChange: (slots: TimeSlot[]) => void;
  disabled?: boolean;
}

const EMPTY_DRAFT: DraftSlot = {
  slot_start: "",
  slot_end: "",
  exclude_weekend: false,
  apply_user_timezone: false,
  timezone_id: undefined,
};

const TimeSlotV2Cst: React.FC<TimeSlotV2CstProps> = ({
  initialTimeSlots,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation("activities-page");
  const theme = useTheme();
  const adapterLocale = i18next.language === "en" ? "en" : "it";

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(
    initialTimeSlots || [],
  );
  const [draft, setDraft] = useState<DraftSlot>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<string[]>([]);
  const [showDatesError, setShowDatesError] = useState(false);

  const { dataDetail: catalogTimeZones } = useFetchDataDetail(
    `${ServicesURL.catalogTimeZones}`,
  );

  const timezoneOptions: TimezoneOption[] = catalogTimeZones
    ? catalogTimeZones.map((tz: Timezone) => ({
        value: tz.id,
        label: tz.label,
      }))
    : [];

  const inputSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
  };

  const validateFields = (): boolean => {
    const missing: string[] = [];
    if (!draft.slot_start || !dayjs(draft.slot_start).isValid())
      missing.push("slot_start");
    if (!draft.slot_end || !dayjs(draft.slot_end).isValid())
      missing.push("slot_end");
    if (!draft.apply_user_timezone && !draft.timezone_id)
      missing.push("timezone_id");

    const start = dayjs(draft.slot_start);
    const end = dayjs(draft.slot_end);
    if (end.isBefore(start)) {
      missing.push("slot_start");
      missing.push("slot_end");
      setShowDatesError(true);
    } else {
      setShowDatesError(false);
    }

    setErrors(missing);
    return missing.length === 0;
  };

  const addSlot = () => {
    if (!validateFields()) return;
    const next: TimeSlot[] = [
      ...timeSlots,
      {
        exclude_weekend: draft.exclude_weekend,
        apply_user_timezone: draft.apply_user_timezone,
        timezone_id: draft.timezone_id,
        slot_start: dayjs(draft.slot_start).format("HH:mm"),
        slot_end: dayjs(draft.slot_end).format("HH:mm"),
      },
    ];
    setDraft(EMPTY_DRAFT);
    setErrors([]);
    setShowDatesError(false);
    setTimeSlots(next);
    onChange(next);
  };

  const removeSlot = (index: number) => {
    const next = timeSlots.filter((_, i) => i !== index);
    setTimeSlots(next);
    onChange(next);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={adapterLocale}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {showDatesError && (
          <Typography
            sx={{ color: theme.palette.error.main, fontSize: "13px" }}
          >
            {t("time-slots.dates-error")}
          </Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TimePicker
            label={t("time-slots.from-hour-label") || ""}
            value={draft.slot_start ? dayjs(draft.slot_start) : null}
            disabled={disabled}
            onChange={(value: any) =>
              setDraft((current) => ({ ...current, slot_start: value }))
            }
            slotProps={{
              textField: {
                size: "small",
                required: true,
                error: errors.includes("slot_start"),
                sx: inputSx,
              },
            }}
            sx={inputSx}
          />
          <TimePicker
            label={t("time-slots.to-hour-label") || ""}
            value={draft.slot_end ? dayjs(draft.slot_end) : null}
            disabled={disabled}
            onChange={(value: any) =>
              setDraft((current) => ({ ...current, slot_end: value }))
            }
            slotProps={{
              textField: {
                size: "small",
                required: true,
                error: errors.includes("slot_end"),
                sx: inputSx,
              },
            }}
            sx={inputSx}
          />
        </Box>

        <Autocomplete
          fullWidth
          size="small"
          disabled={disabled || draft.apply_user_timezone}
          options={timezoneOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, selected) =>
            option.value === selected.value
          }
          value={
            timezoneOptions.find((o) => o.value === draft.timezone_id) ?? null
          }
          onChange={(_, option) =>
            setDraft((current) => ({
              ...current,
              timezone_id: option ? option.value : undefined,
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("time-slots.timezone-label") || ""}
              required={!draft.apply_user_timezone}
              error={errors.includes("timezone_id")}
              sx={inputSx}
            />
          )}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0, sm: 3 },
          }}
        >
          <FormControlLabel
            sx={{ color: theme.palette.text.primary, m: 0 }}
            control={
              <Switch
                checked={draft.exclude_weekend}
                disabled={disabled}
                onChange={(_, checked) =>
                  setDraft((current) => ({
                    ...current,
                    exclude_weekend: checked,
                  }))
                }
              />
            }
            label={t("time-slots.exclude-weekends-toggle")}
          />
          <FormControlLabel
            sx={{ color: theme.palette.text.primary, m: 0 }}
            control={
              <Switch
                checked={draft.apply_user_timezone}
                disabled={disabled}
                onChange={(_, checked) =>
                  setDraft((current) => ({
                    ...current,
                    apply_user_timezone: checked,
                    timezone_id: undefined,
                  }))
                }
              />
            }
            label={t("time-slots.apply-user-timezone-toggle")}
          />
        </Box>

        <Button
          variant="outlined"
          startIcon={<Plus size={16} />}
          onClick={addSlot}
          disabled={disabled}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.primary.main,
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {t("time-slots.add-slot-btn")}
        </Button>

        {timeSlots.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {timeSlots.map((range, index) => {
              const timezone: Timezone | undefined = (
                catalogTimeZones || []
              ).find((tz: Timezone) => tz.id === range.timezone_id);
              return (
                <Box
                  key={`slot_${index}_${range.slot_start}_${range.slot_end}`}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    p: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t("time-slots.row.hours-label", {
                        from: range.slot_start,
                        to: range.slot_end,
                      })}
                    </Typography>
                    {timezone && (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: theme.palette.text.tertiary,
                        }}
                      >
                        {t("time-slots.row.timezone-label")} {timezone.label} (
                        {timezone.zone_id})
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: theme.palette.text.tertiary,
                      }}
                    >
                      {t("time-slots.row.exclude-weekends-label")}{" "}
                      <Box
                        component="span"
                        sx={{
                          color: range.exclude_weekend
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        {range.exclude_weekend
                          ? t("time-slots.row.yes")
                          : t("time-slots.row.no")}
                      </Box>
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: theme.palette.text.tertiary,
                      }}
                    >
                      {t("time-slots.row.apply-user-timezone-label")}{" "}
                      <Box
                        component="span"
                        sx={{
                          color: range.apply_user_timezone
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        {range.apply_user_timezone
                          ? t("time-slots.row.yes")
                          : t("time-slots.row.no")}
                      </Box>
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => removeSlot(index)}
                    disabled={disabled}
                    aria-label="delete"
                    sx={{ color: theme.palette.error.main, flexShrink: 0 }}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default TimeSlotV2Cst;
